import "./licence-list.css";
import React, { useMemo } from "react";
import { formatIsoDateTime } from "../Format";
import { InstanceStore, useInstanceStore } from "../instance/InstanceContext";
import { Licence, LicenceStatus } from "./Licence";
import { issueTextOrDefault } from "../DAO";
import { Link } from "react-router-dom";
import { LicenceOptionsStore, useLicenceOptionsStore } from "./LicenceOptions";

type Props = {
   licences: Licence[];
   selectedId?: number | null;
   form?: JSX.Element | null;
}
export const LicenceList = ({ licences, selectedId, form }: Props): JSX.Element => {
   const instanceStore: InstanceStore = useInstanceStore();
   const licenceOptions: LicenceOptionsStore = useLicenceOptionsStore();

   const instanceNamesById: Map<number, string> = useMemo(() => {
      const map = new Map<number, string>();
      instanceStore.instances?.forEach(i => {
         map.set(i.id, i.name);
      });
      return map;
   }, [instanceStore, instanceStore.instances]);

   const moduleCodesById: Map<number, string> = useMemo(() => {
      const map = new Map<number, string>();
      licenceOptions.modules?.forEach(i => {
         map.set(i.id, i.code);
      });
      return map;
   }, [licenceOptions, licenceOptions.modules]);

   const programTypeNamesById: Map<number, string> = useMemo(() => {
      const map = new Map<number, string>();
      licenceOptions.programTypes?.forEach(i => {
         map.set(i.id, i.name);
      });
      return map;
   }, [licenceOptions, licenceOptions.programTypes]);

   const statusNameDescrMap: Map<LicenceStatus, { name: string, descr: string }> = new Map();
   statusNameDescrMap.set('inactive', {
      name: "Inactive",
      descr: "Inactive.\nWe are outside the active date range, or a revocation was synchronized to the client."
   });
   statusNameDescrMap.set('revoked', {
      name: "Revoked",
      descr: "Has been revoked, but yet synchronized.\nThe client still thinks it's active."
   });
   statusNameDescrMap.set('new', {
      name: "New",
      descr: "Has been created, not yet synchronized."
   });
   statusNameDescrMap.set('modified', {
      name: "Modified",
      descr: "Modified since creation, not yet synchronized."
   });
   statusNameDescrMap.set('synced', {
      name: "Sync'd",
      descr: "Has been synchronized with the client."
   });

   function rowClass(lic: Licence): string {
      const cls = [];
      selectedId === lic.id && cls.push("selected");
      !lic.active && cls.push("inactive");
      return cls.join(" ");
   }

   const colspan = 14;
   const htmlId = (suffix?: string): string => {
      const prefix = "licence-list";
      return suffix ? prefix + "_" + suffix : prefix;
   }
   return (<>
      {(instanceStore.isLoading || licenceOptions.isLoading)
         && <div id={htmlId("loading")}>Loading...</div>
         || (instanceStore.error?.issues?.length || licenceOptions.error?.issues?.length) && (
            <div id={htmlId("load-errors")}>
               {instanceStore.error?.issues
                  .map(issueTextOrDefault)
                  .map(msg => <div className="error">{msg}</div>)}
               <button type="button" className="link" onClick={instanceStore.loadInstances}>try again</button>
               {licenceOptions.error?.issues
                  .map(issueTextOrDefault)
                  .map(msg => <div className="error">{msg}</div>)}
               <button type="button" className="link" onClick={licenceOptions.loadOptions}>try again</button>
            </div>
         )
         ||
         <table border={1} id={htmlId()}>
            <thead>
               <tr>
                  <th>Instance</th>
                  <th className="date">Start Date</th>
                  <th className="date">End Date</th>
                  <th>Mode</th>
                  <th className="number">Max Active<br />Users</th>
                  <th className="number">Max Active<br />Patients</th>
                  <th className="number">Program<br />Types</th>
                  <th className="number">Modules</th>
                  <th>Edited By</th>
                  <th>Revoked By</th>
                  <th className="date">Modified</th>
                  <th className="date">Last Sync</th>
                  <th>Status</th>
                  <th>Actions</th>
               </tr>
            </thead>
            <tbody>
               {licences?.length && licences.map(lic => <React.Fragment key={lic.id}>
                  <tr key={`lic_${lic.id}`} id={htmlId(`row-${lic.id}`)} className={rowClass(lic)}>
                     <td id={htmlId(`inst-${lic.id}`)}>{instanceNamesById.get(lic.instanceId) ?? "???"}</td>
                     <td id={htmlId(`start-${lic.id}`)} className="date">{lic.startDate}</td>
                     <td id={htmlId(`end-${lic.id}`)} className="date">{lic.endDate}</td>
                     <td id={htmlId(`type-${lic.id}`)}
                        title={lic.reportRequired ? "Report Usage" : "Fixed Limit"}>
                        {lic.reportRequired ? "Usage" : "Fixed"}
                     </td>
                     <td id={htmlId(`users-${lic.id}`)} className="number">{lic.maxActiveUsers}</td>
                     <td id={htmlId(`pats-${lic.id}`)} className="number">{lic.maxActivePatients}</td>
                     <td id={htmlId(`progs-${lic.id}`)}
                        className="number"
                        title={lic.programTypeIds
                           .map(id => programTypeNamesById.get(id) ?? "???")
                           .sort()
                           .join("\n")}>
                        {lic.programTypeIds.length}
                     </td>
                     <td id={htmlId(`mods-${lic.id}`)}
                        className="number"
                        title={lic.moduleIds
                           .map(id => moduleCodesById.get(id) ?? "???")
                           .sort()
                           .join("\n")}>
                        {lic.moduleIds.length}
                     </td>
                     <td id={htmlId(`edit-by-${lic.id}`)}>{lic.editedBy}</td>
                     <td id={htmlId(`rev-by-${lic.id}`)}>{lic.revokedBy}</td>
                     <td id={htmlId(`last-mod-${lic.id}`)} className="date">{formatIsoDateTime(lic.lastMod)}</td>
                     <td id={htmlId(`last-sync-${lic.id}`)} className="date">{formatIsoDateTime(lic.lastSync)}</td>
                     <td id={htmlId(`status-${lic.id}`)} title={statusNameDescrMap.get(lic.status)?.descr}>
                        {statusNameDescrMap.get(lic.status)?.name}
                     </td>
                     <td>
                        <Link to={`/licences/${lic.id}/view`} id={htmlId(`view-${lic.id}`)} className="link">view</Link>
                     </td>
                  </tr>
                  {selectedId === lic.id && form &&
                     <tr key={`lic_${lic.id}_form`}>
                        <td colSpan={colspan}>
                           {form}
                        </td>
                     </tr>
                  }
               </React.Fragment>)
                  || licences?.length === 0 &&
                  <tr id={htmlId("no-results")}>
                     <td colSpan={colspan}>No records found.</td>
                  </tr>
               }
            </tbody>
         </table>
      }
   </>);
};
